import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { authApi } from '../lib/api'

interface UserLocation {
  id: string
  name: string
  address: string
  city?: string
  state?: string
  zip?: string
  instructions?: string
  isDefault?: boolean
  latitude?: string
  longitude?: string
}

interface UserMetadata {
  locations?: {
    pickup?: UserLocation[]
    delivery?: UserLocation[]
    'drop-off'?: UserLocation[]
  }
}

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  name: string  // Computed from firstName + lastName
  phone?: string
  avatar?: string
  avatarUrl?: string
  createdAt?: string
  role: string
  verificationStatus: string
  authProvider?: string
  metadata?: UserMetadata
  // False until the person has given an address (Google/Facebook sign-ups start without one).
  profileComplete?: boolean
  // False until the person has accepted the current Terms of Use, Privacy Policy and Acceptable Use Policy.
  termsAccepted?: boolean
  currentTermsVersion?: string
  // What this person may change in the admin console (everyone on staff can look).
  adminPermissions?: string[]
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; requiresVerification?: boolean; email?: string }>
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>
  preRegister: (data: PreRegisterData) => Promise<{ success: boolean; error?: string }>
  completeRegister: (data: CompleteRegisterData) => Promise<{ success: boolean; error?: string; user?: User; token?: string }>
  resendVerification: (email: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  setToken: (token: string) => void
  refreshUser: () => Promise<boolean>
  isLoading: boolean
}

interface PreRegisterData {
  email: string
  firstName: string
  lastName: string
}

interface CompleteRegisterData {
  token: string
  password: string
  phone?: string
  primaryUse?: string
  acceptTerms?: boolean
  address: string
  city: string
  state: string
  zip: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const API_URL = '/api/v1'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    if (storedToken) {
      fetchUser(storedToken)
    } else {
      setIsLoading(false)
    }
  }, [])

  // A real 401 is already handled by the response interceptor (silent refresh, then an
  // 'auth:logout' event if that fails) -- by the time it reaches here as an error the
  // token really is invalid. Anything else (a transient 429, a network blip, a momentary
  // 5xx) is NOT "logged out" -- the token in localStorage is still fine, so this retries
  // once before giving up, instead of silently rendering the login page on top of a still-
  // valid session. Found via a live E2E walkthrough: a rate-limit blip on this exact call
  // bounced an authenticated user back to /login mid-task.
  const attemptFetchUser = async (authToken: string, isRetry = false): Promise<boolean> => {
    try {
      const response = await authApi.getProfile()
      const data = response.data
      // Add computed name field
      const userData: User = {
        ...data.data,
        name: `${data.data.firstName} ${data.data.lastName}`
      }
      setUser(userData)
      setToken(authToken)
      localStorage.setItem('token', authToken)
      return true
    } catch (error: any) {
      if (error?.response?.status !== 401 && !isRetry) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        return attemptFetchUser(authToken, true)
      }
      console.error('Error fetching user:', error)
      return false
    }
  }

  const fetchUser = async (authToken: string): Promise<boolean> => {
    try {
      return await attemptFetchUser(authToken)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshUser = async (): Promise<boolean> => {
    const authToken = token || localStorage.getItem('token')
    if (!authToken) return false
    return fetchUser(authToken)
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.requiresVerification) {
          return { success: false, error: data.error || 'Email not verified', requiresVerification: true, email: data.email || email }
        }
        return { success: false, error: data.error || 'Login failed' }
      }

      const accessToken = data.data.tokens.accessToken
      localStorage.setItem('token', accessToken)
      if (data.data.tokens.refreshToken) {
        localStorage.setItem('refreshToken', data.data.tokens.refreshToken)
      }
      setToken(accessToken)
      setUser({
        ...data.data.user,
        name: `${data.data.user.firstName} ${data.data.user.lastName}`
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const register = async (data: RegisterData) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      const responseData = await response.json()

      if (!response.ok) {
        return { success: false, error: responseData.error || 'Registration failed' }
      }

      // Do NOT auto-login; require email verification
      return { success: true }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const preRegister = async (data: PreRegisterData) => {
    try {
      const response = await fetch(`${API_URL}/auth/pre-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      const responseData = await response.json()

      if (!response.ok) {
        return { success: false, error: responseData.error || 'Registration initiation failed' }
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const completeRegister = async (data: CompleteRegisterData) => {
    try {
      const response = await fetch(`${API_URL}/auth/complete-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      const responseData = await response.json()

      if (!response.ok) {
        return { success: false, error: responseData.error || 'Failed to complete registration' }
      }

      const accessToken = responseData.data.tokens.accessToken
      localStorage.setItem('token', accessToken)
      if (responseData.data.tokens.refreshToken) {
        localStorage.setItem('refreshToken', responseData.data.tokens.refreshToken)
      }
      setToken(accessToken)
      setUser({
        ...responseData.data.user,
        name: `${responseData.data.user.firstName} ${responseData.data.user.lastName}`
      })

      return { success: true, user: responseData.data.user, token: accessToken }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const resendVerification = async (email: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })

      const responseData = await response.json()

      if (!response.ok) {
        return { success: false, error: responseData.error || 'Failed to resend verification email' }
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    setToken(null)
    setUser(null)
    window.location.replace('/login')
  }

  useEffect(() => {
    const handleLogout = () => logout()
    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [])

  // The server refused an action until the current Terms are accepted (see lib/api.ts): show the prompt.
  useEffect(() => {
    const handleTermsRequired = () => setUser((u) => (u ? { ...u, termsAccepted: false } : u))
    window.addEventListener('auth:terms-required', handleTermsRequired)
    return () => window.removeEventListener('auth:terms-required', handleTermsRequired)
  }, [])

  // A background request silently refreshed the access token (see lib/api.ts).
  // Keep this context's token state in sync without re-fetching the user.
  useEffect(() => {
    const handleTokenRefreshed = (event: Event) => {
      const newToken = (event as CustomEvent<{ token: string }>).detail?.token
      if (newToken) setToken(newToken)
    }
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed)
    return () => window.removeEventListener('auth:token-refreshed', handleTokenRefreshed)
  }, [])

  const handleSetToken = (newToken: string) => {
    localStorage.setItem('token', newToken)
    setToken(newToken)
    fetchUser(newToken)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, preRegister, completeRegister, resendVerification, logout, setToken: handleSetToken, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
